/**
 * useNorthernIrelandDetection Hook
 * 
 * Detects whether the current customer's postcode is in Northern Ireland.
 * Used to apply NI-specific label changes for:
 * - Council Tax → Council Rates
 * - Water charges → "(not applicable to Northern Ireland)"
 * - Savings → Include Child Trust Fund note
 */

import { useState, useEffect, useRef } from 'react';
import { httpGet } from '@/shared/api/http';
import { onboardingEndpoints } from '@/shared/api/endpoints';
import { ProfileService } from '@/shared/api/services';
import { femaCapture } from '@/shared/fema/capture';

export interface NorthernIrelandDetectionResult {
  /** Whether the customer's postcode is in Northern Ireland */
  isNorthernIreland: boolean;
  /** Whether the detection is still loading */
  loading: boolean;
  /** Error message if detection failed */
  error: string | null;
}

/**
 * Hook to detect if the current customer is from Northern Ireland based on their postcode.
 * 
 * @returns Object containing isNorthernIreland flag, loading state, and any error
 * 
 * @example
 * ```tsx
 * const { isNorthernIreland, loading, error } = useNorthernIrelandDetection();
 * 
 * if (loading) return <Skeleton />;
 * 
 * // Apply NI-specific labels
 * const label = isNorthernIreland ? "Council Rates" : "Council Tax";
 * ```
 */
export function useNorthernIrelandDetection(): NorthernIrelandDetectionResult {
  const [isNorthernIreland, setIsNorthernIreland] = useState<boolean>(false);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  
  // Prevent duplicate API calls
  const hasFetchedRef = useRef<boolean>(false);
  const abortControllerRef = useRef<AbortController | null>(null);

  useEffect(() => {
    // Skip if already fetched
    if (hasFetchedRef.current) {
      return;
    }

    const detectNorthernIreland = async (): Promise<void> => {
      try {
        setLoading(true);
        setError(null);
        hasFetchedRef.current = true;

        // Resolve userId from localStorage (ProfileService.getCustomerData expects userId, not customerId)
        const userId = (() => {
          try {
            const raw = localStorage.getItem('userData');
            if (!raw) return null;
            const obj = JSON.parse(raw);
            return obj?.userId ?? null;
          } catch {
            return null;
          }
        })();

        if (!userId) {
          // No user ID - default to non-NI
          setIsNorthernIreland(false);
          setLoading(false);
          return;
        }

        // Fetch customer profile to get postcode
        const profileResponse = await ProfileService.getCustomerData(userId, {
          skipCache: true,
          skipErrorCapture: true,
        });

        if (!profileResponse.success || !profileResponse.data) {
          // Profile fetch failed - default to non-NI
          setIsNorthernIreland(false);
          setLoading(false);
          return;
        }

        const profile = Array.isArray(profileResponse.data)
          ? profileResponse.data[0]
          : profileResponse.data;

        // Extract and normalize postcode
        const rawPostcode = String(
          profile?.postCode || profile?.postcode || ''
        ).trim();
        const postcodeTrimmed = rawPostcode.replace(/\s+/g, '').toUpperCase();

        if (!postcodeTrimmed) {
          // No postcode - default to non-NI
          setIsNorthernIreland(false);
          setLoading(false);
          return;
        }

        // Create abort controller for cleanup
        abortControllerRef.current = new AbortController();

        // Fetch country data from postcode
        const countryRes = await httpGet<any>(
          onboardingEndpoints.getPostcodesByCountry(postcodeTrimmed),
          {
            timeout: 10000,
            signal: abortControllerRef.current.signal,
          }
        );

        // Parse country response (handle various API response shapes)
        const countryRaw = Array.isArray(countryRes?.data?.data)
          ? countryRes.data.data[0]
          : Array.isArray(countryRes?.data)
            ? countryRes.data[0]
            : countryRes?.data || {};

        // Determine if Northern Ireland based on country field AND inUse flag
        // (consistent with Dashboard.tsx and useDashboardVisibility.ts)
        const isNI =
          !!countryRaw &&
          countryRaw?.country === 'Northern Ireland' &&
          (countryRaw?.inUse ? countryRaw.inUse === 'Y' : true);

        setIsNorthernIreland(isNI);
        setLoading(false);
      } catch (err: unknown) {
        // Check if this was an abort (component unmounted)
        if (err instanceof Error && err.name === 'AbortError') {
          return;
        }

        // Log error but don't block the user
        femaCapture('ni_detection_error', err, {
          category: 'api',
          severity: 'warning',
          skipSanitization: false,
          user: {},
        });

        // Default to non-NI on error
        setIsNorthernIreland(false);
        setError('Failed to detect region');
        setLoading(false);
      }
    };

    detectNorthernIreland();

    // Cleanup on unmount
    return () => {
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
      }
    };
  }, []);

  return {
    isNorthernIreland,
    loading,
    error,
  };
}

export default useNorthernIrelandDetection;
